import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import * as React from 'react'
import { makeStyles } from 'tss-react/mui'

import Tooltip from '@mui/material/Tooltip'

import { classToFComponent, FCClassContext, IconActionButton, ListagemTab, ListagemTabs } from 'src/utils/views'
import { Logger, NOOP_VOID } from 'wdc-cube'

import { ListagemTabsScope, type ListagemTabsOption } from '../tcm_scopes'

import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import { AppStorage } from 'src/utils'
import ListagemEmpresaView from './tcm_listagem_empresa'
import PesListagemView from './tcm_listagem_pes'
import PrfListagemView from './tcm_listagem_prf'
import QsaListagemView from './tcm_listagem_qsa'

const LOG = Logger.get('TCM-VIEW-LIST-TABS')
const appStorage = AppStorage.singleton()

const tabIdxByTabKey = new Map<number, ListagemTabsOption>()
const tabKeyByTabIdx = new Map<ListagemTabsOption, number>()

const TabStrip = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 10
})

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0
    },
    tab_content: {
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
        minWidth: 0,
        minHeight: 0
    }
})

export type EmpresaTableViewProps = {
    className?: string
    style?: React.CSSProperties
    scope: ListagemTabsScope
}

export class ListagemTabsViewClass implements FCClassContext<EmpresaTableViewProps> {
    scope!: ListagemTabsScope
    classes!: ReturnType<typeof useStyles>['classes']

    readonly emitExpand = () => this.scope.onExpandClick().catch(LOG.caught)
    readonly emitShrunk = () => this.scope.onShrunkClick().catch(LOG.caught)

    onSyncState(props: EmpresaTableViewProps) {
        initializeStatics()

        this.scope = props.scope
        this.classes = useStyles().classes
    }

    render({ className }: EmpresaTableViewProps): React.JSX.Element {
        const { scope, classes } = this
        const tabIdx = tabKeyByTabIdx.get(scope.active) ?? 0
        let idxTabControll = 0
        let empresasTabIdx = -1
        let qsaTabIdx = -1
        let pesTabIdx = -1
        let prfTabIdx = -1

        if (
            !appStorage.tcmCompaniesEnabled &&
            !appStorage.tcmPartnersEnabled &&
            !appStorage.tcmDoctorsEnabled &&
            !appStorage.tcmRelationshipsEnabled
        ) {
            return <></>
        }

        return (
            <div className={clsx(classes.view, className)}>
                <TabStrip>
                    <ListagemTabs
                        value={tabIdx}
                        onChange={this.handleTabChange}
                        aria-label="Detalhamento"
                        role="tablist"
                    >
                        {appStorage.tcmCompaniesEnabled && (
                            <ListagemTab label="Empresas" {...a11yProps((empresasTabIdx = idxTabControll++))} />
                        )}
                        {appStorage.tcmPartnersEnabled && (
                            <ListagemTab label="Sócios" {...a11yProps((qsaTabIdx = idxTabControll++))} />
                        )}
                        {appStorage.tcmDoctorsEnabled && (
                            <ListagemTab label="Médicos" {...a11yProps((prfTabIdx = idxTabControll++))} />
                        )}
                        {appStorage.tcmRelationshipsEnabled && (
                            <ListagemTab label="Vínculos" {...a11yProps((pesTabIdx = idxTabControll++))} />
                        )}
                    </ListagemTabs>
                    <Tooltip title={scope.expanded ? 'Restaurar detalhamento' : 'Maximizar detalhamento'}>
                        <IconActionButton
                            size="small"
                            aria-label={scope.expanded ? 'Restaurar detalhamento' : 'Maximizar detalhamento'}
                            onClick={scope.expanded ? this.emitShrunk : this.emitExpand}
                        >
                            {scope.expanded && <CloseFullscreenRoundedIcon fontSize="small" />}
                            {!scope.expanded && <LaunchRoundedIcon fontSize="small" />}
                        </IconActionButton>
                    </Tooltip>
                </TabStrip>
                <div className={classes.tab_content}>
                    {appStorage.tcmCompaniesEnabled && tabIdx === empresasTabIdx && (
                        <ListagemEmpresaView scope={scope.empresasListagem} />
                    )}
                    {appStorage.tcmPartnersEnabled && tabIdx === qsaTabIdx && (
                        <QsaListagemView scope={scope.qsaListagem} />
                    )}
                    {appStorage.tcmDoctorsEnabled && tabIdx === prfTabIdx && (
                        <PrfListagemView scope={scope.prfListagem} />
                    )}
                    {appStorage.tcmRelationshipsEnabled && tabIdx === pesTabIdx && (
                        <PesListagemView scope={scope.pesListagem} />
                    )}
                </div>
            </div>
        )
    }

    readonly handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        const key = tabIdxByTabKey.get(newValue) ?? 'pj'
        this.scope.onSelect(key).catch(LOG.caught)
    }
}

let initializeStatics = () => {
    const add = (idx: number, key: ListagemTabsOption) => {
        tabIdxByTabKey.set(idx, key)
        tabKeyByTabIdx.set(key, idx)
    }
    let idxCount = 0

    appStorage.tcmCompaniesEnabled && add(idxCount++, 'pj')
    appStorage.tcmPartnersEnabled && add(idxCount++, 'qsa')
    appStorage.tcmDoctorsEnabled && add(idxCount++, 'prf')
    appStorage.tcmRelationshipsEnabled && add(idxCount++, 'pes')

    initializeStatics = NOOP_VOID
}

export default classToFComponent(ListagemTabsViewClass, React)

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    }
}
